import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Copy, Trash2, Plus, Users, Stethoscope, Link as LinkIcon, Mail } from "lucide-react";

export default function Share() {
  const [invites, setInvites] = useState([]);
  const [draft, setDraft] = useState({ role: "carer", name: "", note: "" });
  const [creating, setCreating] = useState(false);
  const [openEmail, setOpenEmail] = useState(null);
  const [emailDraft, setEmailDraft] = useState({ to: "", personal_message: "" });
  const [emailing, setEmailing] = useState(false);

  const load = () => api.get("/share/invites").then((r) => setInvites(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.name.trim()) return;
    setCreating(true);
    try {
      await api.post("/share/invites", draft);
      setDraft({ role: "carer", name: "", note: "" });
      toast.success("Share link created");
      await load();
    } catch { toast.error("Could not create link"); }
    finally { setCreating(false); }
  };

  const revoke = async (token) => { await api.delete(`/share/invites/${token}`); toast.success("Access revoked"); await load(); };
  const linkFor = (token) => `${window.location.origin}/v/${token}`;
  const copy = (token) => { navigator.clipboard.writeText(linkFor(token)); toast.success("Link copied"); };

  const sendEmail = async (token) => {
    if (!emailDraft.to.trim()) return;
    setEmailing(true);
    try {
      await api.post(`/share/invites/${token}/email`, emailDraft);
      toast.success("Invite email sent");
      setOpenEmail(null);
      setEmailDraft({ to: "", personal_message: "" });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send email");
    } finally { setEmailing(false); }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="share-page">
      <header>
        <h1 className="font-display text-3xl font-bold text-slate-900">Share my plan</h1>
        <p className="text-slate-600 mt-2 max-w-xl">Create a private link your family, carer or clinician can use to see your safety plan and leave notes. You control access — revoke any link at any time.</p>
      </header>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="share-create">
        <h2 className="font-display text-lg font-bold text-slate-900 mb-4">Create a link</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <select value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })} className="border border-slate-300 rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-teal-500" data-testid="share-role">
            <option value="carer">Carer / family</option>
            <option value="clinician">Clinician</option>
          </select>
          <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Their name" className="border border-slate-300 rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-teal-500" data-testid="share-name" />
          <input value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} placeholder="Note (optional)" className="border border-slate-300 rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-teal-500 md:col-span-1" data-testid="share-note" />
          <button data-testid="share-create-btn" disabled={creating} onClick={create} className="bg-teal-700 hover:bg-teal-800 disabled:opacity-60 text-white rounded-xl px-4 py-2.5 flex items-center justify-center gap-1 font-semibold">
            <Plus className="w-4 h-4" /> Create link
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="share-list">
        <h2 className="font-display text-lg font-bold text-slate-900 mb-3">Active links</h2>
        {invites.filter((i) => i.active).length === 0 && (
          <div className="text-sm text-slate-500" data-testid="share-empty">No active links yet.</div>
        )}
        <div className="space-y-3">
          {invites.filter((i) => i.active).map((inv) => (
            <div key={inv.token} className="border border-slate-200 rounded-xl p-4" data-testid={`share-item-${inv.token}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${inv.role === "clinician" ? "bg-amber-50 text-amber-800" : "bg-teal-50 text-teal-800"}`}>
                    {inv.role === "clinician" ? <Stethoscope className="w-4 h-4" /> : <Users className="w-4 h-4" />}
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">{inv.name}</div>
                    <div className="text-xs text-slate-500">{inv.role === "clinician" ? "Clinician" : "Carer / family"} · created {new Date(inv.created_at).toLocaleDateString("en-GB")}
                      {inv.emailed_to && <span className="ml-2 text-teal-700">· emailed to {inv.emailed_to}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid={`share-email-${inv.token}`} onClick={() => setOpenEmail(openEmail === inv.token ? null : inv.token)} className="text-sm text-teal-700 border border-teal-200 hover:bg-teal-50 rounded-xl px-3 py-1.5 flex items-center gap-1">
                    <Mail className="w-3.5 h-3.5" /> Email
                  </button>
                  <button data-testid={`share-copy-${inv.token}`} onClick={() => copy(inv.token)} className="text-sm text-teal-700 border border-teal-200 hover:bg-teal-50 rounded-xl px-3 py-1.5 flex items-center gap-1">
                    <Copy className="w-3.5 h-3.5" /> Copy
                  </button>
                  <button data-testid={`share-revoke-${inv.token}`} onClick={() => revoke(inv.token)} className="text-sm text-red-600 border border-red-200 hover:bg-red-50 rounded-xl px-3 py-1.5 flex items-center gap-1">
                    <Trash2 className="w-3.5 h-3.5" /> Revoke
                  </button>
                </div>
              </div>
              <div className="mt-2 text-xs text-slate-500 flex items-center gap-1 break-all"><LinkIcon className="w-3 h-3" /> {linkFor(inv.token)}</div>

              {openEmail === inv.token && (
                <div className="mt-3 border-t border-slate-100 pt-3 space-y-2" data-testid={`share-email-form-${inv.token}`}>
                  <input
                    data-testid={`share-email-to-${inv.token}`}
                    type="email"
                    value={emailDraft.to}
                    onChange={(e) => setEmailDraft({ ...emailDraft, to: e.target.value })}
                    placeholder="their.email@example.com"
                    className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <textarea
                    data-testid={`share-email-message-${inv.token}`}
                    rows={2}
                    value={emailDraft.personal_message}
                    onChange={(e) => setEmailDraft({ ...emailDraft, personal_message: e.target.value })}
                    placeholder="Personal message (optional)"
                    className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <button
                    data-testid={`share-email-send-${inv.token}`}
                    disabled={emailing || !emailDraft.to.trim()}
                    onClick={() => sendEmail(inv.token)}
                    className="bg-teal-700 hover:bg-teal-800 disabled:opacity-60 text-white rounded-xl px-4 py-2 text-sm font-semibold flex items-center gap-1"
                  >
                    <Mail className="w-4 h-4" /> {emailing ? "Sending…" : "Send invite"}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-900">
        Share links are read-only views of your safety plan and contacts. Carers and clinicians can leave notes but cannot edit your plan. Email invites are sent securely via Resend.
      </div>
    </div>
  );
}
